import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Megaphone,
  Calendar,
  Gift,
  Award,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Waves,
  PartyPopper,
} from 'lucide-react';

import { useAuthStore } from '../../store/authStore';
import styles from './Sidebar.module.css';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const allRoles = ['superadmin', 'admin', 'executive', 'mentor', 'associate'];

const navSections = [
  {
    label: 'TEAM',
    items: [
      { path: '/dashboard', icon: Home, label: 'Dashboard', roles: allRoles },
      { path: '/feed', icon: Award, label: 'Spotlight', roles: allRoles },
      { path: '/celebrations', icon: PartyPopper, label: 'Celebrations', roles: allRoles },
      { path: '/streams', icon: Waves, label: 'Streams', roles: allRoles },
    ],
  },
  {
    label: 'ORGANISATION',
    items: [
      { path: '/announcements', icon: Megaphone, label: 'Announcements', roles: allRoles },
      { path: '/events', icon: Calendar, label: 'Events', roles: allRoles },
    ],
  },
  {
    label: 'YOU',
    items: [
      { path: '/my-space', icon: Sparkles, label: 'My Space', roles: allRoles },
      { path: '/rewards', icon: Gift, label: 'Perks & Privileges', roles: allRoles },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { user } = useAuthStore();

  const sidebarClasses = [
    styles.sidebar,
    collapsed ? styles.collapsed : '',
  ].filter(Boolean).join(' ');

  return (
    <aside className={sidebarClasses}>
      <div className={styles.logo}>
        <img src="/logo.svg" alt="RIPPLE" className={styles.logoImage} />
      </div>

      <nav className={styles.nav}>
        {navSections.map((section) => {
          const items = section.items.filter(item => user && item.roles.includes(user.role));
          if (items.length === 0) return null;
          return (
            <div key={section.label} className={styles.navSection}>
              {!collapsed && <div className={styles.navSectionLabel}>{section.label}</div>}
              {collapsed && <div className={styles.navSectionDivider} />}
              {items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.active : ''}`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon size={20} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <button className={styles.toggleBtn} onClick={onToggle} title={collapsed ? 'Expand' : 'Collapse'}>
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </aside>
  );
};

export default Sidebar;
